import { GooglePlacesApiKey } from '../appConfig';
import { LoadWindowScript } from './util';
import { retry } from '../utils/retry';
import { AutocompletePrediction, AutocompleteResponse, AutocompletionRequest, FindPlaceFromQueryRequest, GeocoderRequest, GeocoderResponse, GeocoderResult, PlaceResult } from 'realhaus-sdk';

export class GoogleMapsApiService {
  private services = { places: { current: null }, geocoder: { current: null } };
  private sessionToken: any = null;

  constructor(private libraryScriptLoaded: boolean = false) {
    this.loadPlacesLibrariesScript();
  }

  private getSessionToken = () => {
    const TokenClass = (window as any).google?.maps?.places?.AutocompleteSessionToken;
    if (!this.sessionToken && TokenClass) {
      this.sessionToken = new TokenClass();
    }
    return this.sessionToken;
  }

  private resetSessionToken = () => {
    this.sessionToken = null;
  }
  init() {
    this.loadPlacesLibrariesScript();
  }
  private loadPlacesLibrariesScript = () => {
    if (typeof window !== 'undefined' && !this.libraryScriptLoaded) {
      if (!document.querySelector('#google-maps') || !document.querySelector('_googleMapsServiceId')) {

        console.log('>> Loading gmapsapi script');
        LoadWindowScript(
          `https://maps.googleapis.com/maps/api/js?key=${GooglePlacesApiKey}&libraries=places`,
          document.querySelector('head'),
          'google-maps',
        );
      }

      this.libraryScriptLoaded = true;
    }
  }

  private get mapElement() {
    const elementId = 'google-maps-element';
    if (typeof window !== 'undefined' && !document.querySelector(`#${elementId}`)) {
      const position = document.querySelector('body');

      const element = document.createElement('div');
      element.setAttribute('id', elementId);
      element.setAttribute('hidden', '');

      position?.append(element);
    }

    return document.getElementById(elementId);
  }

  private initPlacesService = () => {
    this.loadPlacesLibrariesScript();
    if (!this.services.places.current && (window as any).google) {
      const map = new (
        window as any
      ).google.maps.Map(this.mapElement)
      this.services.places.current = new (
        window as any
      ).google.maps.places.PlacesService(map);
    }

    if (!this.services.places.current) {
      throw Error('Could not initialize Google Places service')
    }
  }

  private initGeocoderService = () => {
    this.loadPlacesLibrariesScript();
    if (!this.services.geocoder.current && (window as any).google) {
      this.services.geocoder.current = new (
        window as any
      ).google.maps.Geocoder();
    }
    if (!this.services.geocoder.current) {
      throw Error('Could not initialize Google Geocode service');
    }
  }

  getPlacePredictions = (request: AutocompletionRequest, callback?: (results?: AutocompletePrediction[], status?: any) => void): Promise<AutocompleteResponse> => {
    return retry(async () => {
      this.loadPlacesLibrariesScript();
      const AutocompleteSuggestion = (window as any).google?.maps?.places?.AutocompleteSuggestion;
      if (!AutocompleteSuggestion) {
        throw Error('Could not initialize Google Autocomplete service');
      }

      const country = (request as any).componentRestrictions?.country;
      const includedRegionCodes = country
        ? (Array.isArray(country) ? country : [country])
        : undefined;

      const includedPrimaryTypes = (request as any).includedPrimaryTypes as string[] | undefined;

      const sessionToken = this.getSessionToken();

      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: request.input,
        ...(includedRegionCodes ? { includedRegionCodes } : {}),
        ...(includedPrimaryTypes && includedPrimaryTypes.length > 0 ? { includedPrimaryTypes } : {}),
        ...(sessionToken ? { sessionToken } : {}),
      });

      const predictions: AutocompletePrediction[] = (suggestions ?? [])
        .map((s: any) => s.placePrediction)
        .filter((p: any) => !!p)
        .map((p: any) => {
          const matches = (p.mainText?.matches ?? []).map((m: any) => {
            const offset = m.startOffset ?? 0;
            const end = m.endOffset ?? offset;
            return { offset, length: Math.max(end - offset, 0) };
          });

          const description = p.text?.text ?? '';
          let termOffset = 0;
          const terms = (description ? description.split(', ') : []).map((value: string) => {
            const t = { offset: termOffset, value };
            termOffset += value.length + 2; // +2 for ", " separator
            return t;
          });

          const legacy: any = {
            description,
            place_id: p.placeId,
            reference: '',
            structured_formatting: {
              main_text: p.mainText?.text ?? '',
              secondary_text: p.secondaryText?.text ?? '',
              main_text_matched_substrings: matches,
            },
            terms,
            types: p.types ?? [],
          };
          legacy.__placePrediction = p;
          return legacy as unknown as AutocompletePrediction;
        });

      if (callback) {
        callback(predictions);
      }

      return { predictions } as AutocompleteResponse;
    });
  }

  getPlaceDetails = (prediction: AutocompletePrediction): Promise<GeocoderResponse> => {
    return retry(async () => {
      this.loadPlacesLibrariesScript();
      const placePrediction = (prediction as any).__placePrediction;
      if (!placePrediction || typeof placePrediction.toPlace !== 'function') {
        throw Error('Could not resolve place details — missing placePrediction handle');
      }

      try {
        const place = placePrediction.toPlace();
        await place.fetchFields({
          fields: ['addressComponents', 'location', 'formattedAddress', 'id'],
        });

        const addressComponents = (place.addressComponents ?? []).map((c: any) => ({
          long_name: c.longText ?? '',
          short_name: c.shortText ?? '',
          types: c.types ?? [],
        }));

        const result = {
          address_components: addressComponents,
          formatted_address: place.formattedAddress ?? '',
          geometry: { location: place.location },
          place_id: place.id,
          types: [],
        } as unknown as GeocoderResult;

        return { results: [result] } as GeocoderResponse;
      } finally {
        this.resetSessionToken();
      }
    });
  }

  findPlaceFromQuery = (request: FindPlaceFromQueryRequest, callback?: (results?: PlaceResult[], status?: any) => void) => {
    return retry(() => {
      this.initPlacesService();
      return (this.services.places.current as any).findPlaceFromQuery(request, callback);
    });
  }

  geocode = (request: GeocoderRequest, callback?: (results?: GeocoderResult[], status?: any) => void): Promise<GeocoderResponse> => {
    return retry(() => {
      this.initGeocoderService();
      return (this.services.geocoder.current as any).geocode(request, callback);
    });
  }
}